import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ArrowLeft, Save, Printer, Download } from "lucide-react";
import { downloadCotizacionPdf, printCotizacionPdf } from "@/lib/pdf";

const COT_LOGO = "/logo-cooper.png";
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const m = (v) => "$" + Number(v || 0).toFixed(2);

export default function ConfiguracionListasPrecios() {
  const navigate = useNavigate();
  const [listas, setListas] = useState([]);
  const [selId, setSelId] = useState("");
  const [items, setItems] = useState([]);
  const [nueva, setNueva] = useState({ name: "", source_list_id: "" });
  const headerRef = useRef(null);
  const bodyRef = useRef(null);

  const loadListas = async () => {
    const { data } = await api.get("/price-lists");
    setListas(data || []);
    if ((data || []).length && !selId) setSelId(data[0].id);
  };
  useEffect(() => { loadListas(); }, []);
  useEffect(() => {
    if (!selId) { setItems([]); return; }
    api.get(`/price-lists/${selId}/products`).then(({ data }) => setItems(data || []));
  }, [selId]);

  const crear = async () => {
    if (!nueva.name) return toast.error("Nombre requerido");
    try { const { data } = await api.post("/price-lists", { name: nueva.name, source_list_id: nueva.source_list_id || null }); toast.success("Lista creada"); setNueva({ name: "", source_list_id: "" }); await loadListas(); setSelId(data.id); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const eliminarLista = async (id) => {
    if (!window.confirm("¿Eliminar esta lista y sus precios?")) return;
    if (selId === id) { setSelId(""); setItems([]); }
    try { await api.delete(`/price-lists/${id}`); loadListas(); toast.success("Lista eliminada"); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };
  const setPrecio = (id, v) => setItems((prev) => prev.map((it) => (it.id === id ? { ...it, precio_unitario: v } : it)));
  const guardarPrecio = async (it) => {
    try { await api.put(`/price-list-items/${it.id}`, { precio_unitario: Number(it.precio_unitario) || 0 }); toast.success("Precio guardado"); }
    catch (err) { toast.error(`Error: ${err?.response?.data?.detail || err.message}`); }
  };

  const selLista = listas.find((l) => l.id === selId);
  const now = new Date();
  const periodo = `${MESES[now.getMonth()]} ${now.getFullYear()}`;
  const periodoCorto = `${MESES[now.getMonth()]} ${String(now.getFullYear()).slice(-2)}`;
  const fileName = `Lista de precios oficial ${periodoCorto}`;
  const handleDownload = () => downloadCotizacionPdf(bodyRef.current, headerRef.current, fileName);
  const handlePrint = () => printCotizacionPdf(bodyRef.current, headerRef.current);

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="listas-precios-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/configuracion")}><ArrowLeft className="h-4 w-4 mr-2" /> Configuración</Button>
      <div><h1 className="text-3xl font-bold tracking-tight">Listas de Precios</h1><p className="text-muted-foreground mt-2">Crea listas y edita precios por producto.</p></div>

      <Card><CardContent className="pt-6 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div className="space-y-2"><Label>Nueva lista</Label><Input value={nueva.name} onChange={(e) => setNueva({ ...nueva, name: e.target.value })} placeholder="Nombre" data-testid="lista-nombre" /></div>
        <div className="space-y-2"><Label>Copiar precios de</Label>
          <Select value={nueva.source_list_id} onValueChange={(v) => setNueva({ ...nueva, source_list_id: v })}>
            <SelectTrigger data-testid="lista-source"><SelectValue placeholder="(opcional)" /></SelectTrigger>
            <SelectContent>{listas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={crear} data-testid="lista-crear"><Plus className="h-4 w-4 mr-2" /> Crear lista</Button>
      </CardContent></Card>

      <Card><CardContent className="pt-6 space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-2 flex-1 min-w-[220px] max-w-sm"><Label>Lista</Label>
            <Select value={selId} onValueChange={setSelId}>
              <SelectTrigger data-testid="lista-select"><SelectValue placeholder="Seleccione una lista" /></SelectTrigger>
              <SelectContent>{listas.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {selId && (
            <>
              <Button variant="outline" onClick={handleDownload} data-testid="lista-descargar-pdf"><Download className="h-4 w-4 mr-2" /> Descargar PDF</Button>
              <Button variant="outline" onClick={handlePrint} data-testid="lista-imprimir"><Printer className="h-4 w-4 mr-2" /> Imprimir</Button>
              <Button variant="destructive" onClick={() => eliminarLista(selId)}><Trash2 className="h-4 w-4 mr-2" /> Eliminar lista</Button>
            </>
          )}
        </div>
        {selId && (
          <div className="border rounded-md overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Ref</TableHead><TableHead>Modelo</TableHead><TableHead className="w-48">Precio Unitario</TableHead><TableHead className="w-12"></TableHead></TableRow></TableHeader>
              <TableBody>
                {items.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center h-20 text-muted-foreground">Lista sin productos.</TableCell></TableRow>
                  : items.map((it) => (
                    <TableRow key={it.id}>
                      <TableCell className="font-mono text-xs">{it.n_ref}</TableCell>
                      <TableCell className="font-medium">{it.modelo}</TableCell>
                      <TableCell><Input type="number" step="0.01" value={it.precio_unitario} onChange={(e) => setPrecio(it.id, e.target.value)} className="h-8" /></TableCell>
                      <TableCell><Button variant="ghost" size="icon" onClick={() => guardarPrecio(it)} title="Guardar precio"><Save className="h-4 w-4" /></Button></TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent></Card>

      {/* Bloque imprimible (fuera de pantalla) — lineal, membrete de cotización por hoja */}
      {selId && (
        <div style={{ position: "absolute", left: "-10000px", top: 0 }} aria-hidden="true">
          <div className="bg-white text-black w-full max-w-[750px] box-border">
            <style>{`.pdf-exporting table td,.pdf-exporting table th{line-height:1.4!important;padding-top:5px!important;padding-bottom:5px!important}.pdf-exporting tr{page-break-inside:avoid;break-inside:avoid}`}</style>
            {/* Membrete (se sella en cada hoja) */}
            <div ref={headerRef} className="px-8">
              <div className="grid grid-cols-[auto_1fr_auto] gap-6 items-start bg-gray-50 px-3 py-3">
                <div className="text-center">
                  <img src={COT_LOGO} className="h-16 w-16 object-contain rounded-lg mx-auto" alt="Cooper Slip" />
                  <div className="text-xs mt-1">Cooper Slip</div>
                </div>
                <div className="text-center text-sm font-bold leading-snug pt-3">
                  <p>Cuando tu cliente queda bien, Tu quedas mejor.</p>
                  <p>Elije las duelas que respaldan a tu negocio</p>
                </div>
                <div className="text-right">
                  <h2 className="text-3xl font-extrabold tracking-tight">LISTA DE PRECIOS</h2>
                  <p className="text-sm mt-1"><span className="font-bold">Vigencia:</span> {periodo}</p>
                </div>
              </div>
              <div className="border-t-4 border-black" />
            </div>
            {/* Cuerpo (fluye en varias hojas) */}
            <div ref={bodyRef} className="px-8 pt-4 pb-16">
              <p className="text-sm mb-4 leading-relaxed avoid-break">
                Estimado cliente, es un gusto saludarle. Nos complace presentarle nuestra lista de precios vigente de <span className="font-semibold">Duelas Cooper Slip</span>, la mejor opción en duelas para la fabricación de puertas y portones. Cada modelo está elaborado con materiales de la más alta calidad para respaldar el prestigio de su negocio. Quedamos a sus órdenes para cualquier pedido o aclaración.
              </p>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-3 py-1 text-left w-28">N/REF</th>
                    <th className="border border-gray-300 px-3 py-1 text-left">Modelo</th>
                    <th className="border border-gray-300 px-3 py-1 text-right w-40">Precio Unitario</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id}>
                      <td className="border border-gray-300 px-3 py-1 font-mono">{it.n_ref}</td>
                      <td className="border border-gray-300 px-3 py-1">{it.modelo}</td>
                      <td className="border border-gray-300 px-3 py-1 text-right font-semibold">{m(it.precio_unitario)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-[10px] text-gray-500 mt-2 avoid-break">Precios en pesos mexicanos (MXN), antes de IVA. Sujetos a cambio sin previo aviso.</p>
              <div className="mt-8 mb-6 text-sm avoid-break" style={{ pageBreakInside: "avoid", breakInside: "avoid" }}>
                <p className="font-semibold">Atentamente,</p>
                <p>Departamento de Ventas</p>
                <p className="text-xs text-gray-600">Duelas Cooper Slip</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
